import { createServerFn } from "@tanstack/react-start";
import { trafficFieldsFromHeaders, trafficTermContentFromHeaders } from "@/lib/traffic-fields";
import { getRequestHeader } from "@tanstack/react-start/server";
import * as React from "react";
import { render } from "@react-email/render";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { getFormSubmissionsClient } from "@/lib/supabase-form-submissions.server";
import { template as financeTemplate } from "@/lib/email-templates/finance-calculator";
import { calculateRepayment, formatGBP, REPRESENTATIVE_APR } from "@/lib/finance-calc";
import { I4F_APPLY_URL } from "@/content/finance";

const SITE_NAME = "crsenergy";
const SENDER_DOMAIN = "notify.completeremedialsolutions.co.uk";
const FROM_DOMAIN = "notify.completeremedialsolutions.co.uk";
const CALCULATOR_URL =
  "https://www.completeremedialsolutions.co.uk/finance-loan-calculator";

const inputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().max(50).optional().or(z.literal("")),
  postcode: z.string().trim().max(20).optional().or(z.literal("")),
  amount: z.number().min(1).max(200000),
  deposit: z.number().min(0).max(200000).optional(),
  termMonths: z.number().int().min(1).max(240),
  aprPercent: z.number().min(0).max(100).optional(),
  honeypot: z.string().max(0).optional().or(z.literal("")),
  source_page: z.string().trim().max(300).optional(),
  gclid: z.string().trim().max(300).optional(),
});

async function fireZapier(payload: Record<string, unknown>) {
  const url = process.env.ZAPIER_ENQUIRY_WEBHOOK_URL;
  if (!url) return;
  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.error("Zapier webhook failed:", err);
  }
}

function generateToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export const sendCalculatorResults = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => inputSchema.parse(input))
  .handler(async ({ data }) => {
    if (data.honeypot && data.honeypot.length > 0) {
      return { success: true as const, id: `honeypot-${crypto.randomUUID()}` };
    }

    const calc = calculateRepayment({
      amount: data.amount,
      deposit: data.deposit ?? 0,
      termMonths: data.termMonths,
      aprPercent: data.aprPercent ?? REPRESENTATIVE_APR,
    });

    const userAgent = getRequestHeader("user-agent")?.slice(0, 1000) ?? null;

    // 1) Record enquiry in DB
    const message = [
      `Price: ${formatGBP(calc.amount)}`,
      `Deposit: ${formatGBP(calc.deposit)}`,
      `Amount of credit: ${formatGBP(calc.amountOfCredit)}`,
      `Finance type: ${calc.financeType} (${calc.aprPercent.toFixed(2)}% APR)`,
      `Term: ${calc.termMonths} months`,
      `Estimated monthly: ${formatGBP(calc.monthly)}`,
      `Total repayable: ${formatGBP(calc.total)}`,
      `Total interest: ${formatGBP(calc.interest)}`,
    ].join("\n");

    const id = crypto.randomUUID();

    const insertRow = {
      id,
      form_type: "finance_calculator" as const,
      name: data.name.trim(),
      phone: data.phone?.trim() || null,
      email: data.email.trim(),
      postcode: data.postcode?.trim() || null,
      service_of_interest: "Financing",
      message,
      source_page:
        data.source_page?.trim() ||
        trafficFieldsFromHeaders(getRequestHeader).landing_page ||
        "/finance-calculator",
      gclid: data.gclid?.trim() || null,
      user_agent: userAgent,
      ...trafficFieldsFromHeaders(getRequestHeader),
    };

    const { error: insertError } = await getFormSubmissionsClient()
      .from("form_submissions")
      .insert(insertRow);
    if (insertError) {
      console.error("Failed to insert calculator submission:", insertError);
      return {
        success: false as const,
        error:
          "We couldn't send your results. Please try again or call us on 0800 009 2992.",
      };
    }

    // 2) Zapier (fire-and-log)
    await fireZapier({
      subject: `Finance calculator: ${insertRow.name}${
        insertRow.postcode ? ` (${insertRow.postcode})` : ""
      }`,
      lead_id: id,
      submitted_at: new Date().toISOString(),
      form_type: "finance_calculator",
      name: insertRow.name,
      phone: insertRow.phone,
      email: insertRow.email,
      postcode: insertRow.postcode,
      service_of_interest: "Financing",
      message: insertRow.message,
      source_page: insertRow.source_page,
      gclid: insertRow.gclid,
      user_agent: insertRow.user_agent,
      source: insertRow.traffic_source,
      source_medium: insertRow.traffic_medium,
      source_campaign: insertRow.traffic_campaign,
      source_referrer: insertRow.traffic_referrer,
      landing_page: insertRow.landing_page,
      ...trafficTermContentFromHeaders(getRequestHeader),
      calculation: calc,
    });

    // 3) Send transactional email via queue (service-role client, internal trigger)
    try {
      const supabaseUrl = process.env.SUPABASE_URL;
      const serviceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
      if (!supabaseUrl || !serviceKey) {
        console.warn("Email infra env vars missing; skipping calculator email.");
        return { success: true as const, id, emailQueued: false };
      }
      const supabase = createClient(supabaseUrl, serviceKey);
      const recipient = insertRow.email.toLowerCase();

      // Suppression check
      const { data: suppressed } = await supabase
        .from("suppressed_emails")
        .select("id")
        .eq("email", recipient)
        .maybeSingle();
      if (suppressed) {
        return { success: true as const, id, emailQueued: false };
      }

      // Unsubscribe token: reuse or create
      let unsubscribeToken: string;
      const { data: existingToken } = await supabase
        .from("email_unsubscribe_tokens")
        .select("token, used_at")
        .eq("email", recipient)
        .maybeSingle();
      if (existingToken && !existingToken.used_at) {
        unsubscribeToken = existingToken.token;
      } else {
        unsubscribeToken = generateToken();
        await supabase
          .from("email_unsubscribe_tokens")
          .upsert(
            { token: unsubscribeToken, email: recipient },
            { onConflict: "email", ignoreDuplicates: true },
          );
        const { data: storedToken } = await supabase
          .from("email_unsubscribe_tokens")
          .select("token")
          .eq("email", recipient)
          .maybeSingle();
        if (storedToken?.token) unsubscribeToken = storedToken.token;
      }

      const templateData = {
        name: insertRow.name,
        amount: formatGBP(calc.amount),
        deposit: formatGBP(calc.deposit),
        amountOfCredit: formatGBP(calc.amountOfCredit),
        termMonths: calc.termMonths,
        monthly: formatGBP(calc.monthly),
        total: formatGBP(calc.total),
        interest: formatGBP(calc.interest),
        aprPercent: calc.aprPercent,
        financeType: calc.financeType,
        applyUrl: I4F_APPLY_URL,
        calculatorUrl: CALCULATOR_URL,
      };

      const element = React.createElement(financeTemplate.component, templateData);
      const html = await render(element);
      const text = await render(element, { plainText: true });
      const rawSubject = financeTemplate.subject as
        | string
        | ((d: Record<string, unknown>) => string);
      const subject =
        typeof rawSubject === "function" ? rawSubject(templateData) : rawSubject;

      const messageId = crypto.randomUUID();

      await supabase.from("email_send_log").insert({
        message_id: messageId,
        template_name: "finance-calculator",
        recipient_email: insertRow.email,
        status: "pending",
      });

      const { error: enqueueError } = await supabase.rpc("enqueue_email", {
        queue_name: "transactional_emails",
        payload: {
          message_id: messageId,
          to: insertRow.email,
          from: `${SITE_NAME} <noreply@${FROM_DOMAIN}>`,
          sender_domain: SENDER_DOMAIN,
          subject,
          html,
          text,
          purpose: "transactional",
          label: "finance-calculator",
          idempotency_key: messageId,
          unsubscribe_token: unsubscribeToken,
          queued_at: new Date().toISOString(),
        },
      });

      if (enqueueError) {
        console.error("Failed to enqueue calculator email:", enqueueError);
        return { success: true as const, id, emailQueued: false };
      }

      return { success: true as const, id, emailQueued: true };
    } catch (err) {
      console.error("Calculator email path failed:", err);
      return { success: true as const, id, emailQueued: false };
    }
  });
